import { Drawer, Descriptions, Progress, Tag, Divider, Empty, Alert } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../shared/types/common";

interface Props {
  onClose: () => void;
  visible: boolean;
  result: any; // a ScreeningResult row
}

const pct = (value?: number) => {
  if (value === null || value === undefined || isNaN(Number(value))) return 0;
  // Scores are stored 0–1; show as a percentage.
  return Math.round(Number(value) * 100);
};

const titleCase = (value?: string) =>
  String(value || "")
    .split("/")
    .map((part) =>
      part
        .trim()
        .split(/\s+/)
        .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ""))
        .join(" ")
    )
    .join(" / ");

const ScoreBreakdownComponent = ({ onClose, visible, result }: Props) => {
  const { screenType } = useSelector((state: RootState) => state.settings);
  const breakdown = result?.breakdown || {};
  const similarityExplanation = breakdown?.similarityExplanation || {};
  const similarityMatches = similarityExplanation?.matchedKeywords || [];

  const bar = (label: string, value?: number) => (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 13,
        }}
      >
        <span>{label}</span>
        <span className="weight-600">{pct(value)}%</span>
      </div>
      <Progress percent={pct(value)} showInfo={false} />
    </div>
  );

  return (
    <Drawer
      title={<strong>Score Breakdown</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait ? "90%" : screenType.isLg || screenType.isXl ? 620 : 500
      }
      onClose={onClose}
      open={visible}
    >
      {!result ? (
        <Empty />
      ) : (
        <>
          {breakdown?.dataQuality === "LOW" && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Low-quality resume text"
              description={
                breakdown?.dataWarning ||
                "Very little readable text was found in this resume — treat this score with caution."
              }
            />
          )}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Candidate">
              {result?.candidateName || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {result?.candidateEmail || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Rank">
              {result?.rank != null ? `#${result.rank}` : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Final Score">
              <Tag color="blue">{pct(result?.finalScore)}%</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Prompt Version">
              {result?.promptVersion || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Scoring Strategy">
              {breakdown?.scoringVersion === "v2" ? (
                <Tag color="green">V2 — Trust, then verify</Tag>
              ) : (
                <Tag color="volcano">V1 — Prove it</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Divider>Component Scores</Divider>
          {bar("Similarity (embedding cosine)", result?.similarityScore ?? breakdown?.similarityScore)}
          {bar("Skill Weight Score", result?.ruleScore ?? breakdown?.ruleScore)}
          {bar("AI Score (locked prompt)", result?.aiScore ?? breakdown?.aiScore)}

          {(similarityMatches.length > 0 || similarityExplanation?.method) && (
            <>
              <Divider style={{ marginTop: 24 }}>
                Similarity Match Explanation
              </Divider>
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: "#6b7280", fontSize: 12, marginBottom: 8 }}>
                  {similarityExplanation?.method ||
                    "Similarity compares the full job description and resume. The terms below are visible resume/JD overlaps; primary and secondary skill weighting is shown in the next section."}
                </p>
                {similarityMatches.length > 0 ? (
                  similarityMatches.map((match: any, i: number) => (
                    <div key={`sim-${i}`} style={{ marginBottom: 8 }}>
                      <Tag>term</Tag>
                      <span className="weight-600">{titleCase(match?.name)}</span>
                      {match?.evidence && (
                        <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                          {match.evidence}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#6b7280", fontSize: 12 }}>
                    No direct JD skill or keyword overlap was found in the readable resume text.
                  </div>
                )}
              </div>
            </>
          )}

          {Boolean(
            breakdown?.matchedRules?.length || breakdown?.missedRules?.length
          ) && (
            <>
              <Divider>Rule Weight Score</Divider>
              {breakdown?.matchedRules?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>Matched</div>
                  {breakdown.matchedRules.map((r: string, i: number) => (
                    <Tag color="green" key={`m-${i}`}>
                      {r}
                    </Tag>
                  ))}
                </div>
              )}
              {breakdown?.missedRules?.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>Missed</div>
                  {breakdown.missedRules.map((r: string, i: number) => (
                    <Tag color="red" key={`x-${i}`}>
                      {r}
                    </Tag>
                  ))}
                </div>
              )}
            </>
          )}

          {Array.isArray(breakdown?.skillEvidence) &&
            breakdown.skillEvidence.length > 0 && (
              <>
                <Divider>AI Score ({breakdown?.scoringVersion === "v2" ? "V2" : "V1"})</Divider>
                {breakdown.skillEvidence.map((s: any, i: number) => (
                  <div key={`sk-${i}`} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {s?.skill}{" "}
                        <Tag color={s?.tier === "must" ? "blue" : "default"}>
                          {s?.tier === "must" ? "must-have" : "nice-to-have"}
                        </Tag>
                        {s?.present ? (
                          <Tag color="green">on resume</Tag>
                        ) : (
                          <Tag color="red">not found</Tag>
                        )}
                      </span>
                      <span className="weight-600">
                        {Math.round(Number(s?.confidence ?? 0))}%
                      </span>
                    </div>
                    <Progress
                      percent={Math.round(Number(s?.confidence ?? 0))}
                      showInfo={false}
                      strokeColor={s?.present ? undefined : "#d9d9d9"}
                    />
                  </div>
                ))}
              </>
            )}

          {breakdown?.aiRationale && (
            <>
              <Divider>AI Rationale</Divider>
              <p style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                {breakdown.aiRationale}
              </p>
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ScoreBreakdownComponent;
